import React, { useMemo, useRef, useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserContext } from '../contexts/UserContext';
import { useMyLeagueStore } from '../store/myLeagueStore';
import { usePopulatedLeague } from '../hooks/usePopulatedLeague';
import { useLeagueSchedule, Game } from '../hooks/useGames';
import TeamLogo from './TeamLogo';
import { getTeamInfoById } from '../utils/getTeamInfo';
import dayjs from 'dayjs';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { FiRadio, FiUsers } from 'react-icons/fi';
import './ScheduleBanner.css';

/* ─── helpers ────────────────────────────────────── */

const RANGE_BACK_DAYS = 14;
const RANGE_FORWARD_DAYS = 14;
const SCROLL_AMOUNT = 400;
const SCHEDULE_MIN_DATE = '2026-03-30';

function dateRange(centerDate: string, back: number, forward: number) {
  const center = dayjs(centerDate);
  const start = center.subtract(back, 'day');
  const end = center.add(forward, 'day');
  return {
    startDate: start.format('YYYY-MM-DD'),
    endDate: end.format('YYYY-MM-DD'),
  };
}

/** Group games by scheduled_date, sorted chronologically */
function groupByDate(games: Game[]): Map<string, Game[]> {
  const map = new Map<string, Game[]>();
  const sorted = [...games].sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date));
  for (const g of sorted) {
    const d = g.scheduled_date.slice(0, 10);
    if (!map.has(d)) map.set(d, []);
    map.get(d)!.push(g);
  }
  return map;
}

/* ─── Main component ─────────────────────────────── */

export interface BannerGameSelection {
  awayAbbr: string;
  homeAbbr: string;
  gameDate: string;
  simGameId?: number | null;
}

interface ScheduleBannerProps {
  onGameClick?: (game: BannerGameSelection) => void;
}

const ScheduleBanner: React.FC<ScheduleBannerProps> = ({ onGameClick }) => {
  const navigate = useNavigate();
  const { myTeam } = useUserContext();
  const myLeague = useMyLeagueStore((s) => s.myLeague);
  const leagueId = myLeague?.id;
  const { data: league } = usePopulatedLeague(leagueId);
  const trackRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const embargoed = myLeague?.embargoed ?? 0;
  const [hoveredGameKey, setHoveredGameKey] = useState<string | null>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);


  // Date range centered on today (clamped to schedule min)
  const today = dayjs().format('YYYY-MM-DD');
  const embargoDate = today;

  const centerDate = today < SCHEDULE_MIN_DATE ? SCHEDULE_MIN_DATE : today;
  const { startDate, endDate } = useMemo(
    () => dateRange(centerDate, RANGE_BACK_DAYS, RANGE_FORWARD_DAYS),
    [centerDate]
  );

  // Fetch schedule (now includes sim scores inline)
  const { data: scheduleData, isLoading } = useLeagueSchedule({
    leagueId,
    startDate,
    endDate,
    enabled: !!leagueId,
  });

  // Map team abbreviation → team id
  const teamAbbrToIdMap = useMemo(() => {
    if (!league) return new Map<string, number>();
    const map = new Map<string, number>();
    league.conferences?.forEach((conf: any) => {
      conf.divisions?.forEach((div: any) => {
        div.teams?.forEach((team: any) => {
          const abbr = team.rw_abbreviation || team.abbreviation;
          if (abbr) map.set(abbr, team.id);
          if (team.abbreviation && team.abbreviation !== team.rw_abbreviation) {
            map.set(team.abbreviation, team.id);
          }
        });
      });
    });
    return map;
  }, [league]);

  // Group games by date
  const gamesByDate = useMemo(() => {
    if (!scheduleData?.games) return new Map<string, Game[]>();
    return groupByDate(scheduleData.games);
  }, [scheduleData]);

  // Scroll handlers
  const scroll = useCallback((dir: 'left' | 'right') => {
    if (!trackRef.current) return;
    const amount = dir === 'left' ? -SCROLL_AMOUNT : SCROLL_AMOUNT;
    trackRef.current.scrollBy({ left: amount, behavior: 'smooth' });
  }, []);

  const updateScrollCtas = useCallback(() => {
    if (!trackRef.current) return;
    setCanScrollLeft(trackRef.current.scrollLeft > 1);
  }, []);

  // Auto-scroll to today on mount
  useLayoutEffect(() => {
    if (!trackRef.current || isLoading) return;
    const todayEl = trackRef.current.querySelector('[data-date-today="true"]');
    if (todayEl) {
      const track = trackRef.current;
      const trackRect = track.getBoundingClientRect();
      const elRect = (todayEl as HTMLElement).getBoundingClientRect();
      const scrollTarget = elRect.left - trackRect.left + track.scrollLeft - 36;
      track.scrollTo({ left: scrollTarget, behavior: 'auto' });
    }
    updateScrollCtas();
  }, [isLoading, gamesByDate.size, updateScrollCtas]);

  // Team info helper
  const getTeamData = useCallback(
    (abbr: string) => {
      const teamId = teamAbbrToIdMap.get(abbr);
      const info = teamId ? getTeamInfoById(teamId, league as any) : null;
      if (info && typeof info === 'object' && 'name' in info) {
        const t = info as { name: string; logo?: string; color_primary?: string; color_secondary?: string; color_text?: string };
        return { name: t.name || abbr, abbreviation: abbr, logo: t.logo, color_primary: t.color_primary, color_secondary: t.color_secondary, color_text: t.color_text };
      }
      return { name: abbr, abbreviation: abbr };
    },
    [teamAbbrToIdMap, league]
  );

  const isMyGame = useCallback(
    (game: Game) => {
      if (!myTeam) return false;
      const awayId = teamAbbrToIdMap.get(game.away_abbr);
      const homeId = teamAbbrToIdMap.get(game.home_abbr);
      return awayId === myTeam || homeId === myTeam;
    },
    [myTeam, teamAbbrToIdMap]
  );

  // Hover handlers with delay to prevent flicker
  const handleGameMouseEnter = useCallback((gameKey: string) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setHoveredGameKey(gameKey);
  }, []);

  const handleGameMouseLeave = useCallback(() => {
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredGameKey(null);
    }, 150);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    };
  }, []);

  /* ─── render ───────────────────────────────────── */

  if (!myTeam || !myLeague) return null;

  const dates = Array.from(gamesByDate.keys());

  return (
    <div className="schedule-banner" id="schedule-banner">
      {/* Left arrow */}
      <button
        className={`schedule-banner__arrow schedule-banner__arrow--left${!canScrollLeft ? ' schedule-banner__arrow--hidden' : ''}`}
        onClick={() => scroll('left')}
        aria-label="Scroll schedule left"
        aria-hidden={!canScrollLeft}
        tabIndex={canScrollLeft ? 0 : -1}
      >
        <FiChevronLeft />
      </button>

      {/* Track */}
      <div className="schedule-banner__track-wrapper">
        {isLoading ? (
          <div className="schedule-banner__skeleton">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="schedule-banner__skeleton-card" />
            ))}
          </div>
        ) : dates.length === 0 ? (
          <div style={{ padding: '12px 48px', color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>
            No games scheduled
          </div>
        ) : (
          <div className="schedule-banner__track" ref={trackRef} onScroll={updateScrollCtas}>
            {dates.map((date) => {
              const games = gamesByDate.get(date)!;
              const isToday = date === today;
              const d = dayjs(date);

              return (
                <div className="schedule-banner__date-group" key={date} data-date-today={isToday}>
                  {/* Date label */}
                  <div
                    className={`schedule-banner__date-label${isToday ? ' schedule-banner__date-label--today' : ''}`}
                  >
                    <span className="schedule-banner__date-label-month">{d.format('MMM')}</span>
                    <span className="schedule-banner__date-label-day">{d.format('D')}</span>
                  </div>

                  {/* Game cards for this date */}
                  {games.map((game, gi) => {
                    const gameDate = dayjs(game.scheduled_date).format('YYYY-MM-DD');
                    const isEmbargoedGame = embargoed > 0 && gameDate === embargoDate;
                    const hasScore = !isEmbargoedGame && game.home_runs != null && game.away_runs != null;
                    const simStatus = game.sim_status?.toLowerCase() || '';
                    const isLive = isEmbargoedGame && simStatus === 'complete' && embargoed === 1;
                    const isFinal = !isEmbargoedGame && simStatus === 'complete';
                    const mine = isMyGame(game);
                    const hasSimGameId = !!game.sim_game_id;
                    const gameKey = `${date}-${gi}`;
                    const isHovered = hoveredGameKey === gameKey;

                    let statusText = `${dayjs(game.scheduled_date).format('MMM D')}`;
                    let statusClass = 'schedule-banner__status--scheduled';
                    if (isFinal) {
                      statusText = 'Final';
                      statusClass = 'schedule-banner__status--final';
                    } else if (isLive) {
                      statusText = 'Live';
                      statusClass = 'schedule-banner__status--live';
                    }

                    return (
                      <div
                        className={`schedule-banner__game${mine ? ' schedule-banner__game--mine' : ''}`}
                        key={gameKey}
                        style={{ cursor: hasSimGameId || onGameClick ? 'pointer' : undefined }}
                        onMouseEnter={hasSimGameId ? () => handleGameMouseEnter(gameKey) : undefined}
                        onMouseLeave={hasSimGameId ? handleGameMouseLeave : undefined}
                        onClick={() => {
                          if (!hasSimGameId) {
                            onGameClick?.({
                              awayAbbr: game.away_abbr,
                              homeAbbr: game.home_abbr,
                              gameDate: dayjs(game.scheduled_date).format('YYYY-MM-DD'),
                            });
                          }
                        }}
                      >
                        {/* Hover popup for played games */}
                        {hasSimGameId && isHovered && (isLive || isFinal) && (
                          <div className="schedule-banner__hover-popup">
                            <button
                              className="schedule-banner__popup-btn schedule-banner__popup-btn--broadcast"
                              onClick={(e) => {
                                e.stopPropagation();
                                setHoveredGameKey(null);
                                navigate(`/scoreboard?gameId=${game.sim_game_id}`);
                              }}
                            >
                              <FiRadio className="schedule-banner__popup-icon" />
                              <span>Broadcast</span>
                            </button>
                            <button
                              className="schedule-banner__popup-btn schedule-banner__popup-btn--lineup"
                              onClick={(e) => {
                                e.stopPropagation();
                                setHoveredGameKey(null);
                                onGameClick?.({
                                  awayAbbr: game.away_abbr,
                                  homeAbbr: game.home_abbr,
                                  gameDate: dayjs(game.scheduled_date).format('YYYY-MM-DD'),
                                  simGameId: game.sim_game_id,
                                });
                              }}
                            >
                              <FiUsers className="schedule-banner__popup-icon" />
                              <span>Lineup</span>
                            </button>
                          </div>
                        )}

                        {/* Status line */}
                        <div className={`schedule-banner__status ${statusClass}`}>{statusText}</div>
                        {!isFinal && !isLive && (
                          <div className="schedule-banner__game-time">2pm</div>
                        )}

                        {/* Away team */}
                        <div className="schedule-banner__team-row">
                          <TeamLogo team={getTeamData(game.away_abbr)} size="sm" />
                          <span className="schedule-banner__team-abbr">{game.away_abbr}</span>
                          {hasScore && (
                            <span
                              className={`schedule-banner__team-score${game.away_runs! > game.home_runs! ? ' schedule-banner__team-score--winner' : ''}`}
                            >
                              {game.away_runs}
                            </span>
                          )}
                        </div>

                        {/* Home team */}
                        <div className="schedule-banner__team-row">
                          <TeamLogo team={getTeamData(game.home_abbr)} size="sm" />
                          <span className="schedule-banner__team-abbr">{game.home_abbr}</span>
                          {hasScore && (
                            <span
                              className={`schedule-banner__team-score${game.home_runs! > game.away_runs! ? ' schedule-banner__team-score--winner' : ''}`}
                            >
                              {game.home_runs}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Right arrow */}
      <button
        className="schedule-banner__arrow schedule-banner__arrow--right"
        onClick={() => scroll('right')}
        aria-label="Scroll schedule right"
      >
        <FiChevronRight />
      </button>
    </div>
  );
};

export default ScheduleBanner;
